// The one way FrameX writes its bookkeeping flags (notApplicable,
// framexExcluded, detectDone, exported) onto document pages.
//
// Every one of those writes is the same shape — clone doc.data, set flags on
// some pages, hand the whole thing to updateDocumentNotUndoable — and every
// one of them must skip pages mid-split. Keeping that in one place is what
// stops the next flag from re-deriving the rule and getting it wrong.

// Extension spelled out so this module also resolves under bare `node`
// (Vite doesn't need it, node --test does) and stays unit-testable.
import cloneDeep from "lodash/cloneDeep.js";
import { get } from "svelte/store";

/**
 * Apply `patch` to each of `pageIds` on a clone of the document's live data
 * and persist it (not undoable — these are bookkeeping flags).
 *
 * Takes a document id or object but re-resolves it against the store, since
 * callers reach here after a slow await and a captured object's `data` may be
 * stale (a peer broadcast can have replaced the wrapper). Missing or `pending`
 * (mid-split) pages are skipped, as are no-op patches; if nothing changes the
 * document isn't touched.
 *
 * @returns {boolean} whether the document was updated.
 */
export function setPageFlags(group, document, pageIds, patch) {
  const docId = typeof document === "string" ? document : document?.id;
  const doc = get(group)?.documents?.[docId];
  if (!doc) return false;

  const data = cloneDeep(doc.data);
  const entries = Object.entries(patch || {});
  let changed = false;

  for (const pageId of pageIds || []) {
    const page = data?.pages?.[pageId];
    if (!page || page.pending) continue;
    if (entries.every(([key, value]) => page[key] === value)) continue;

    for (const [key, value] of entries) page[key] = value;
    changed = true;
  }

  if (!changed) return false;

  group.updateDocumentNotUndoable(docId, { data });
  return true;
}
